import type { ReactNode } from "react";

export interface NavItem {
  label: string;
  /** Route to navigate to. Omit for parent-only items that just disclose children. */
  to?: string;
  icon: ReactNode;
  /** Small text label, e.g. "Soon" or "Beta". */
  badge?: string;
  /** Render as visually disabled and non-interactive. */
  disabled?: boolean;
  children?: NavItem[];
}

export interface NavSection {
  /** Optional small caption above this group. */
  heading?: string;
  items: NavItem[];
}

const ic = {
  // 18×18 stroke icons. Replace with a real icon set (lucide-react) when we
  // have the budget for a dep — for now, hand-rolled keeps the bundle tiny.
  dashboard: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <rect x="3" y="3" width="7" height="9" rx="1.6" stroke="currentColor" strokeWidth="1.6" />
      <rect x="14" y="3" width="7" height="5" rx="1.6" stroke="currentColor" strokeWidth="1.6" />
      <rect x="14" y="12" width="7" height="9" rx="1.6" stroke="currentColor" strokeWidth="1.6" />
      <rect x="3" y="16" width="7" height="5" rx="1.6" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  ),
  pulse: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M3 12h3l2-6 4 12 2-9 2 5h5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  bolt: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  ),
  plug: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M9 3v5M15 3v5M7 8h10v3a5 5 0 0 1-10 0V8ZM12 16v5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M3 20c.5-3.6 3-5.5 6-5.5s5.5 1.9 6 5.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="17" cy="9.5" r="2.4" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M14.5 18c.4-2.4 2-3.6 4-3.6s2.5 1.2 2.5 3.6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  ),
  cog: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M19.4 13.4a7.5 7.5 0 0 0 0-2.8l1.7-1.3-1.7-3-2 .8a7.5 7.5 0 0 0-2.4-1.4l-.3-2.1h-3.4l-.3 2.1a7.5 7.5 0 0 0-2.4 1.4l-2-.8-1.7 3 1.7 1.3a7.5 7.5 0 0 0 0 2.8L2.9 14.7l1.7 3 2-.8a7.5 7.5 0 0 0 2.4 1.4l.3 2.1h3.4l.3-2.1a7.5 7.5 0 0 0 2.4-1.4l2 .8 1.7-3-1.7-1.3Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  ),
  phone: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A15 15 0 0 1 3 6a2 2 0 0 1 2-2Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  ),
  dot: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="3" fill="currentColor" />
    </svg>
  ),
  alert: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M12 3 2.5 19.5h19L12 3ZM12 10v4M12 17.5h.01"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  chat: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-4.7A8 8 0 1 1 21 12Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="12" r="1" fill="currentColor" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
      <circle cx="15" cy="12" r="1" fill="currentColor" />
    </svg>
  ),
  rocket: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M5 15c-1.5 1.5-2 5-2 5s3.5-.5 5-2M9 12l3 3M14.5 4.5C12 7 9 11 9 11l4 4s4-3 6.5-5.5c1.7-1.7 1.5-5 1.5-5s-3.3-.2-5 .5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx="15" cy="9" r="1.3" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  ),
  phoneOut: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A15 15 0 0 1 3 6a2 2 0 0 1 2-2Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M15 9V4m0 0h-5m5 0-6 6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

/**
 * Sidebar navigation config. Add items here — every consumer reads this
 * single source of truth.
 *
 * Items with `disabled: true` and `badge: "Soon"` advertise upcoming surface
 * area without faking pages. Replace with a `to` when the route ships.
 */
export const NAV: NavSection[] = [
  {
    items: [
      { label: "Dashboard", to: "/", icon: ic.dashboard },
      {
        label: "Insights",
        icon: ic.pulse,
        children: [
          { label: "Health", to: "/insights/health", icon: ic.dot },
          { label: "Cost", to: "/insights/cost", icon: ic.dot },
          {
            label: "Deliverability",
            to: "/insights/deliverability",
            icon: ic.dot,
          },
          { label: "Engagement", to: "/insights/engagement", icon: ic.dot },
        ],
      },
      { label: "Messages", to: "/messages", icon: ic.bolt },
      { label: "Alerts", to: "/alerts", icon: ic.alert },
      { label: "Phone numbers", to: "/phone-numbers", icon: ic.phone },
      {
        label: "AgentFlow",
        icon: ic.chat,
        badge: "Beta",
        children: [
          { label: "Flows", to: "/agentflow", icon: ic.dot },
          { label: "Slot catalog", to: "/agentflow/slots", icon: ic.dot },
          { label: "Knowledge base", to: "/agentflow/knowledge-base", icon: ic.dot },
          { label: "Conversations", to: "/agentflow/conversations", icon: ic.dot },
          { label: "Pending bookings", to: "/agentflow/pending-appointments", icon: ic.dot },
          { label: "Analytics", to: "/agentflow/analytics", icon: ic.dot },
        ],
      },
      { label: "Accelerators", to: "/accelerators", icon: ic.rocket },
      { label: "Screened Calls", to: "/dialer", icon: ic.phoneOut },
      { label: "Connections", to: "/connections", icon: ic.plug },
    ],
  },
  {
    heading: "Workspace",
    items: [
      { label: "Team", icon: ic.users, disabled: true, badge: "Soon" },
      { label: "Settings", to: "/workspace/settings", icon: ic.cog },
    ],
  },
];
